import { useState } from "react";
import { Link } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  getBlogCategories,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../client/components/ui/dialog";
import { useToast } from "../../../client/hooks/use-toast";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import useTranslation from "../../../client/hooks/useTranslation";

function toSlug(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function AdminBlogCategoriesPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: categories, refetch } = useQuery(getBlogCategories, undefined);

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const createAction = useAction(createBlogCategory);
  const updateAction = useAction(updateBlogCategory);
  const deleteAction = useAction(deleteBlogCategory);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createAction({ name: newName.trim(), slug: toSlug(newName) });
      toast({ title: t.adminBlog.categorySaved });
      setNewName("");
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;
    try {
      await updateAction({ id, name: editName.trim(), slug: toSlug(editName) });
      toast({ title: t.adminBlog.categorySaved });
      setEditingId(null);
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAction({ id: deleteTarget.id });
      toast({ title: t.adminBlog.categoryDeleted });
      setDeleteTarget(null);
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={t.adminBlog.manageCategories} />

      <div className="flex items-center justify-between mb-6">
        <Link to="/admin/blog">
          <Button variant="outline" size="sm">← {t.adminBlog.title}</Button>
        </Link>
      </div>

      <div className="max-w-xl space-y-4">
        <div className="flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t.adminBlog.categoryName}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
          <Button onClick={handleCreate} disabled={!newName.trim()}>
            <Plus className="w-4 h-4 mr-1" />
            {t.adminBlog.addCategory}
          </Button>
        </div>

        <div className="rounded-xl border border-border overflow-hidden">
          {(categories ?? []).map((cat: any) => (
            <div key={cat.id} className="flex items-center justify-between px-4 py-3 border-b border-border/50 last:border-0 hover:bg-card/50">
              {editingId === cat.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleUpdate(cat.id)}
                    autoFocus
                    className="h-8"
                  />
                  <Button size="sm" variant="ghost" onClick={() => handleUpdate(cat.id)}>
                    <Check className="w-4 h-4 text-primary" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium text-foreground text-sm">{cat.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{cat.slug} · {cat._count?.posts ?? 0} article(s)</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget({ id: cat.id, name: cat.name })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
          {(categories ?? []).length === 0 && (
            <div className="px-4 py-8 text-center text-muted-foreground text-sm">
              Aucune catégorie.
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminBlog.confirmDeleteCategory}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">"{deleteTarget?.name}"</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete}>{t.adminBlog.deleteCategory}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DefaultLayout>
  );
}
